/**
 * Spaces Page
 * Shows list of spaces (high-level containers for saved queries)
 */

import React from 'react';
import {
  Avatar,
  AvatarGroup,
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Chip,
  IconButton,
  InputBase,
  InputAdornment,
  TextField as MuiTextField,
  Card,
  CardActionArea,
  CardContent,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { AppDialog, ConfirmDialog } from '../components/dialog';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { useNavigate } from 'react-router-dom';
import { formatRelativeTime, formatAbsolute } from '../utils/date';
import { listSpaces, createSpace, updateSpace, deleteSpace, Space, SpaceContributor } from '../services/spacesService';

type OwnerFilter = 'all' | 'mine' | 'team';

/** Pull the current user's id from localStorage (set on login). */
function getCurrentUserId(): string | null {
  try {
    const raw = window.localStorage.getItem('klaris_user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.user_id === 'string' ? parsed.user_id : null;
  } catch {
    return null;
  }
}

/** "the reviewer Verma" → "AV"; falls back to "?" so the Avatar always renders something. */
function initialsFromName(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0]!.toUpperCase();
  return (parts[0][0]! + parts[parts.length - 1][0]!).toUpperCase();
}

/** Stable hue per name so two users with different names get different
 *  avatar colors without us having to maintain an explicit palette. */
function colorForName(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) & 0xffffffff;
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 45%)`;
}

interface NameAvatarProps {
  name: string;
  size?: number;
  fontSize?: string;
}
function NameAvatar({ name, size = 22, fontSize }: NameAvatarProps) {
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        fontSize: fontSize ?? `${Math.max(10, Math.round(size * 0.45))}px`,
        fontWeight: 600,
        bgcolor: colorForName(name),
        color: '#fff',
      }}
    >
      {initialsFromName(name)}
    </Avatar>
  );
}

interface SpaceCardFooterProps {
  space: Space;
  currentUserId: string | null;
}
/**
 * Card footer — one dense row:
 *
 *   [AV][SK +1]  the reviewer +1 · last updated 7 days ago    💬 3   →
 *
 * The "last updated" phrasing makes the relative time unambiguous (it's
 * about activity in the space, not about when the user last visited).
 * Created date is preserved in a tooltip on the time text rather than
 * taking its own line, since most viewers care about recency, not origin.
 */
function SpaceCardFooter({ space, currentUserId }: SpaceCardFooterProps) {
  const lastActivity = space.last_activity_at;
  const isMine = currentUserId === space.created_by_user_id;
  const creatorLabel = isMine ? 'You' : space.created_by_name;
  const contributors: SpaceContributor[] = space.contributors || [];

  // People label — "the reviewer" or "the reviewer +2", kept terse so the relative time
  // (which carries higher recency signal) gets its own visual chunk after.
  const peopleLabel = contributors.length > 0
    ? `${creatorLabel} +${contributors.length}`
    : creatorLabel;

  // The recency slot is always populated. When the space has items we show
  // the latest activity ("last updated 2h ago"); for an empty space we
  // gracefully fall back to its creation time ("created 2h ago") so the
  // card never has a half-empty metadata row.
  const timeLabel = lastActivity
    ? `last updated ${formatRelativeTime(lastActivity)}`
    : `created ${formatRelativeTime(space.created_at)}`;

  // Tooltip on the time gathers both timestamps in one place so the
  // information isn't lost just because the line is removed from the card.
  const timeTooltip = lastActivity
    ? `Last activity ${formatAbsolute(lastActivity)} · Created ${formatAbsolute(space.created_at)}`
    : `Created ${formatAbsolute(space.created_at)}`;

  return (
    <Box
      sx={(t) => ({
        // `mt: 'auto'` pushes the footer to the bottom of the card when the
        // surrounding CardContent is a flex column, so cards with no
        // description don't visually collapse upward. The minimum top
        // margin keeps a comfortable gap from the description even when
        // the card content is short and `auto` collapses to 0.
        mt: 'auto',
        pt: 1.5,
        borderTop: `1px solid ${t.tokens.border.subtle}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
        <AvatarGroup
          max={3}
          sx={{
            '& .MuiAvatar-root': {
              width: 20,
              height: 20,
              fontSize: '0.5625rem',
              fontWeight: 600,
              borderWidth: 1.5,
            },
          }}
        >
          <Tooltip title={`Created by ${creatorLabel}`}>
            <NameAvatar name={space.created_by_name} size={20} />
          </Tooltip>
          {contributors.map((c) => (
            <Tooltip key={c.user_id} title={c.name}>
              <NameAvatar name={c.name} size={20} />
            </Tooltip>
          ))}
        </AvatarGroup>
        <Tooltip title={timeTooltip}>
          <Typography
            noWrap
            sx={(t) => ({
              fontSize: '0.75rem',
              color: t.tokens.text.secondary,
              minWidth: 0,
            })}
          >
            {peopleLabel}
            <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              {' · '}
              {timeLabel}
            </Box>
          </Typography>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        <Tooltip title={`${space.query_count || 0} ${space.query_count === 1 ? 'chat' : 'chats'}`}>
          <Box
            sx={(t) => ({
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.4,
              px: 0.75,
              py: 0.125,
              borderRadius: `${t.ds.radius.full}px`,
              bgcolor: t.tokens.surface.sunken,
              color: t.tokens.text.secondary,
              fontSize: '0.6875rem',
              fontWeight: 600,
              lineHeight: 1.4,
            })}
          >
            <ChatBubbleOutlineRounded sx={{ fontSize: 11 }} />
            {space.query_count || 0}
          </Box>
        </Tooltip>
        {/* Open arrow — subtle at rest, brand-accent on card hover. The
            card itself is the click target via CardActionArea; this is
            purely an affordance cue. */}
        <Box
          className="space-card-open"
          aria-hidden
          sx={(t) => ({
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: `${t.ds.radius.full}px`,
            color: t.tokens.text.tertiary,
            transition: `color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, transform ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
          })}
        >
          <ArrowForwardRounded sx={{ fontSize: 14 }} />
        </Box>
      </Box>
    </Box>
  );
}

export default function SpacesPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [spaces, setSpaces] = React.useState<Space[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [sortBy, setSortBy] = React.useState<'name' | 'created_at' | 'query_count'>('created_at');
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');
  const [ownerFilter, setOwnerFilter] = React.useState<OwnerFilter>('all');

  // Snapshot the current user once. Avoids a localStorage hit per render and
  // means an entire tab session uses one identity (matches the rest of the app).
  const currentUserId = React.useMemo(() => getCurrentUserId(), []);
  
  // Create space state
  const [createOpen, setCreateOpen] = React.useState(false);
  const [createName, setCreateName] = React.useState('');
  const [createDesc, setCreateDesc] = React.useState('');
  const [createErr, setCreateErr] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  
  // Edit space state
  const [editOpen, setEditOpen] = React.useState(false);
  const [editSpace, setEditSpace] = React.useState<Space | null>(null);
  const [editName, setEditName] = React.useState('');
  const [editDesc, setEditDesc] = React.useState('');
  const [editErr, setEditErr] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  
  // Delete space state
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [delSpace, setDelSpace] = React.useState<Space | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  // Fetch spaces
  const fetchSpaces = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listSpaces();
      setSpaces(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load spaces');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchSpaces();
  }, [fetchSpaces]);

  // Counts shown next to the All / Mine / Team toggle. Computed off the
  // unfiltered list so the badges stay stable as the user types in search.
  const ownerCounts = React.useMemo(() => {
    const mine = spaces.filter((s) => s.created_by_user_id === currentUserId).length;
    return { all: spaces.length, mine, team: spaces.length - mine };
  }, [spaces, currentUserId]);

  // Filter and sort spaces
  const filteredAndSortedSpaces = React.useMemo(() => {
    let filtered = spaces;

    if (ownerFilter === 'mine' && currentUserId) {
      filtered = filtered.filter((s) => s.created_by_user_id === currentUserId);
    } else if (ownerFilter === 'team' && currentUserId) {
      filtered = filtered.filter((s) => s.created_by_user_id !== currentUserId);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(space =>
        space.name.toLowerCase().includes(query) ||
        space.description?.toLowerCase().includes(query) ||
        space.created_by_name.toLowerCase().includes(query)
      );
    }

    return [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'query_count':
          comparison = (a.query_count || 0) - (b.query_count || 0);
          break;
        case 'created_at':
          // Sort by activity rather than creation when available — what
          // users actually want when they pick "most recent".
          comparison =
            new Date(a.last_activity_at || a.created_at).getTime() -
            new Date(b.last_activity_at || b.created_at).getTime();
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [spaces, ownerFilter, currentUserId, searchQuery, sortBy, sortOrder]);

  const handleSpaceClick = (spaceId: string) => {
    navigate(`/spaces/${spaceId}`);
  };
  
  const handleSort = (column: 'name' | 'created_at' | 'query_count') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };
  
  const handleCreate = async () => {
    setCreateErr(null);
    const name = createName.trim();
    if (!name) { setCreateErr('Name is required'); return; }
    setCreating(true);
    try {
      await createSpace(name, createDesc.trim() || undefined);
      await fetchSpaces();
      setCreateOpen(false);
      setCreateName('');
      setCreateDesc('');
    } catch (e: any) {
      setCreateErr(e?.message || 'Failed to create space');
    } finally {
      setCreating(false);
    }
  };
  
  const handleEdit = async () => {
    setEditErr(null);
    if (!editSpace) return;
    const name = editName.trim();
    if (!name) { setEditErr('Name is required'); return; }
    setSaving(true);
    try {
      await updateSpace(editSpace.space_id, name, editDesc.trim() || undefined);
      await fetchSpaces();
      setEditOpen(false);
      setEditSpace(null);
    } catch (e: any) {
      setEditErr(e?.message || 'Failed to update space');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async () => {
    if (!delSpace) return;
    setDeleting(true);
    try {
      await deleteSpace(delSpace.space_id);
      await fetchSpaces();
      setDeleteOpen(false);
      setDelSpace(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to delete space');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      {/* Page Header - compact toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.5, sm: 2 },
          mb: 2,
          pb: 1.5,
          borderBottom: (t: any) => `1px solid ${t.tokens.border.subtle}`,
          minHeight: { sm: 36 },
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.45 }}>
          Organize your saved insights into categories
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <InputBase
            placeholder="Search categories…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon sx={(t) => ({ color: t.tokens.text.tertiary, ml: 0.5, fontSize: 16 })} />
              </InputAdornment>
            }
            sx={(t) => ({
              width: { xs: '100%', sm: 240 },
              px: 1.25,
              py: 0.5,
              border: `1px solid ${t.tokens.border.default}`,
              borderRadius: `${t.ds.radius.sm}px`,
              fontSize: '0.8125rem',
              height: 32,
              bgcolor: t.tokens.surface.raised,
              transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, box-shadow ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
              '&:hover': { borderColor: t.tokens.border.strong },
              '&:focus-within': { borderColor: t.tokens.brand.primary, boxShadow: t.tokens.shadow.focusRing },
            })}
          />
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineRounded />}
            size="small"
            onClick={() => setCreateOpen(true)}
            sx={(t) => ({
              borderRadius: `${t.ds.radius.sm}px`,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              height: 32,
              fontSize: '0.8125rem',
              fontWeight: 600,
              boxShadow: 'none',
              alignSelf: { xs: 'stretch', sm: 'auto' },
              '&:hover': { boxShadow: 'none' },
            })}
          >
            {isMobile ? 'New' : 'New category'}
          </Button>
        </Box>
      </Box>

      {/* Owner filter — visible only when there's a meaningful split between
          spaces the user created and ones their teammates did. With one or
          zero spaces in the tenant it's just clutter. */}
      {!loading && spaces.length >= 2 && currentUserId && (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between' }}>
          <ToggleButtonGroup
            value={ownerFilter}
            exclusive
            size="small"
            onChange={(_, v: OwnerFilter | null) => v && setOwnerFilter(v)}
            sx={(t) => ({
              alignSelf: { xs: 'stretch', sm: 'flex-start' },
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontSize: '0.8125rem',
                fontWeight: 600,
                px: 1.5,
                py: 0.5,
                color: t.tokens.text.secondary,
                borderColor: t.tokens.border.default,
                '&.Mui-selected': {
                  color: t.tokens.brand.primary,
                  bgcolor: t.tokens.brand.primarySoft,
                  borderColor: t.tokens.brand.primary,
                  '&:hover': { bgcolor: t.tokens.brand.primarySoft },
                },
              },
            })}
          >
            <ToggleButton value="all">All <Box component="span" sx={{ ml: 0.75, opacity: 0.7, fontWeight: 500 }}>{ownerCounts.all}</Box></ToggleButton>
            <ToggleButton value="mine">Mine <Box component="span" sx={{ ml: 0.75, opacity: 0.7, fontWeight: 500 }}>{ownerCounts.mine}</Box></ToggleButton>
            <ToggleButton value="team">Team <Box component="span" sx={{ ml: 0.75, opacity: 0.7, fontWeight: 500 }}>{ownerCounts.team}</Box></ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State — token-driven skeletons */}
      {loading ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Paper
              key={i}
              variant="outlined"
              sx={(t) => ({
                p: 2.5,
                minHeight: 140,
                borderRadius: `${t.ds.radius.lg}px`,
                borderColor: t.tokens.border.subtle,
                bgcolor: t.tokens.surface.raised,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
              })}
            >
              <Box sx={(t) => ({ width: 32, height: 32, bgcolor: t.tokens.surface.sunken, borderRadius: `${t.ds.radius.sm}px` })} />
              <Box sx={(t) => ({ width: '60%', height: 14, bgcolor: t.tokens.surface.sunken, borderRadius: `${t.ds.radius.xs}px` })} />
              <Box sx={(t) => ({ width: '90%', height: 10, bgcolor: t.tokens.surface.sunken, borderRadius: `${t.ds.radius.xs}px` })} />
            </Paper>
          ))}
        </Box>
      ) : filteredAndSortedSpaces.length === 0 ? (
        <Paper
          variant="outlined"
          sx={(t) => ({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            px: 3,
            borderStyle: 'dashed',
            borderColor: t.tokens.border.default,
            borderRadius: `${t.ds.radius.lg}px`,
            bgcolor: t.tokens.surface.sunken,
          })}
        >
          <Box sx={(t) => ({
            width: 44, height: 44, mb: 1.5,
            borderRadius: `${t.ds.radius.md}px`,
            bgcolor: t.tokens.brand.primarySoft,
            color: t.tokens.brand.primary,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          })}>
            <FolderIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography sx={(t) => ({ mb: 0.5, fontSize: '0.9375rem', fontWeight: 600, color: t.tokens.text.primary })}>
            {searchQuery
              ? 'No categories found'
              : ownerFilter === 'mine'
              ? "You haven't created any categories yet"
              : ownerFilter === 'team'
              ? 'No categories from your teammates'
              : 'No categories yet'}
          </Typography>
          <Typography sx={(t) => ({ fontSize: '0.8125rem', color: t.tokens.text.secondary, textAlign: 'center', maxWidth: 360 })}>
            {searchQuery
              ? 'Try adjusting your search terms'
              : ownerFilter === 'mine'
              ? 'Save a chat to a new category to get started.'
              : ownerFilter === 'team'
              ? 'When teammates save chats to a category, they\u2019ll show up here.'
              : 'Save insights from the chat to create your first category'}
          </Typography>
        </Paper>
      ) : (
        /* Spaces Cards — clean neutral chrome, brand-accent stripe on hover,
           hover-reveal actions. No "No description" placeholder when empty. */
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2, minWidth: 0 }}>
          {filteredAndSortedSpaces.map((space) => (
            <Card
              key={space.space_id}
              variant="outlined"
              sx={(t) => ({
                position: 'relative',
                // Stretch to row height so cards with and without a
                // description align to the same height. Combined with the
                // CardContent flex-column below, this pins the footer to
                // the bottom and the empty space soaks into the middle.
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                borderRadius: `${t.ds.radius.lg}px`,
                overflow: 'hidden',
                borderColor: t.tokens.border.subtle,
                bgcolor: t.tokens.surface.raised,
                transition: `border-color ${t.ds.motion.duration.normal}ms ${t.ds.motion.easing.standard}, box-shadow ${t.ds.motion.duration.normal}ms ${t.ds.motion.easing.standard}, transform ${t.ds.motion.duration.normal}ms ${t.ds.motion.easing.standard}`,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0, left: 0, bottom: 0,
                  width: 3,
                  bgcolor: t.tokens.brand.primary,
                  opacity: 0,
                  transition: `opacity ${t.ds.motion.duration.normal}ms ${t.ds.motion.easing.standard}`,
                },
                '&:hover': {
                  borderColor: t.tokens.border.strong,
                  boxShadow: t.tokens.shadow.md,
                  transform: 'translateY(-1px)',
                  '&::before': { opacity: 1 },
                  '& .space-card-actions': { opacity: 1 },
                  '& .space-card-open': {
                    color: t.tokens.brand.primary,
                    transform: 'translateX(2px)',
                  },
                },
                '&:focus-within': { borderColor: t.tokens.brand.primary, boxShadow: t.tokens.shadow.focusRing },
              })}
            >
              <CardActionArea
                onClick={() => handleSpaceClick(space.space_id)}
                sx={{
                  alignItems: 'stretch',
                  flex: 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                disableRipple
              >
                <CardContent
                  sx={{
                    p: 1.75,
                    '&:last-child': { pb: 1.75 },
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Title row: folder + name + Team chip stay tight together
                      on the left; the hover-only action stack is pushed to
                      the right via `ml: auto`. This way the Team chip is
                      always anchored next to the title regardless of whether
                      the actions are visible — no more "floating right with
                      a huge gap" when the user isn't hovering. */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Box sx={(t) => ({
                      width: 26,
                      height: 26,
                      borderRadius: `${t.ds.radius.sm}px`,
                      bgcolor: t.tokens.brand.primarySoft,
                      color: t.tokens.brand.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    })}>
                      <FolderIcon sx={{ fontSize: 15 }} />
                    </Box>
                    <Typography
                      noWrap
                      sx={(t) => ({
                        // `flex: 0 1 auto` ⇒ size to content but allow
                        // shrinking with ellipsis when the title is long.
                        // No `flex: 1` (which would steal all empty space
                        // and push the chip to the edge).
                        flex: '0 1 auto',
                        minWidth: 0,
                        fontWeight: 600,
                        fontSize: '0.9375rem',
                        lineHeight: 1.3,
                        letterSpacing: '-0.005em',
                        color: t.tokens.text.primary,
                      })}
                    >
                      {space.name}
                    </Typography>
                    {currentUserId && space.created_by_user_id !== currentUserId && (
                      <Tooltip title="This category was created by a teammate. Chats opened from here are shared.">
                        <Chip
                          label="Team"
                          size="small"
                          sx={(t) => ({
                            flexShrink: 0,
                            height: 18,
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            color: t.tokens.semantic.warning,
                            bgcolor: t.tokens.semantic.warningSoft,
                            border: `1px solid ${t.tokens.border.subtle}`,
                            '& .MuiChip-label': { px: 0.75 },
                          })}
                        />
                      </Tooltip>
                    )}
                    <Box
                      className="space-card-actions"
                      sx={{ display: 'flex', gap: 0.25, ml: 'auto', flexShrink: 0, opacity: { xs: 1, md: 0 }, transition: 'opacity 120ms ease' }}
                    >
                      <Tooltip title="Edit category">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setEditSpace(space);
                            setEditName(space.name);
                            setEditDesc(space.description || '');
                            setEditOpen(true);
                          }}
                          sx={(t) => ({
                            width: 24, height: 24,
                            color: t.tokens.text.tertiary,
                            '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
                          })}
                        >
                          <EditRounded sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete category">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setDelSpace(space);
                            setDeleteOpen(true);
                          }}
                          sx={(t) => ({
                            width: 24, height: 24,
                            color: t.tokens.text.tertiary,
                            '&:hover': { color: t.tokens.semantic.danger, bgcolor: t.tokens.semantic.dangerSoft },
                          })}
                        >
                          <DeleteOutlineRounded sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  {space.description ? (
                    <Typography
                      sx={(t) => ({
                        mt: 0.75,
                        // Guarantees a fixed gap between the description and
                        // the footer's divider line. `mt: 'auto'` on the
                        // footer can collapse to 0 when the row stretches,
                        // so without an explicit mb here the description
                        // would butt right up against the divider on
                        // shorter cards.
                        mb: 1.5,
                        fontSize: '0.8125rem',
                        lineHeight: 1.5,
                        color: t.tokens.text.secondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-word',
                      })}
                    >
                      {space.description}
                    </Typography>
                  ) : null}
                  <SpaceCardFooter
                    space={space}
                    currentUserId={currentUserId}
                  />
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
      
      <AppDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        size="sm"
        title="Create New Category"
        busy={creating}
        primaryAction={{ label: 'Create', onClick: handleCreate }}
        secondaryAction={{ label: 'Cancel', onClick: () => setCreateOpen(false), color: 'inherit' }}
      >
        {createErr && <Alert severity="error" sx={{ mb: 2 }}>{createErr}</Alert>}
        <MuiTextField
          autoFocus
          label="Category Name"
          fullWidth
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          sx={{ mt: 0.5, mb: 2 }}
          disabled={creating}
        />
        <MuiTextField
          label="Description (optional)"
          fullWidth
          multiline
          rows={3}
          value={createDesc}
          onChange={(e) => setCreateDesc(e.target.value)}
          disabled={creating}
        />
      </AppDialog>

      <AppDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        size="sm"
        title="Edit Category"
        busy={saving}
        primaryAction={{ label: 'Save', onClick: handleEdit }}
        secondaryAction={{ label: 'Cancel', onClick: () => setEditOpen(false), color: 'inherit' }}
      >
        {editErr && <Alert severity="error" sx={{ mb: 2 }}>{editErr}</Alert>}
        <MuiTextField
          autoFocus
          label="Category Name"
          fullWidth
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          sx={{ mt: 0.5, mb: 2 }}
          disabled={saving}
        />
        <MuiTextField
          label="Description (optional)"
          fullWidth
          multiline
          rows={3}
          value={editDesc}
          onChange={(e) => setEditDesc(e.target.value)}
          disabled={saving}
        />
      </AppDialog>

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        tone="destructive"
        title="Delete category?"
        confirmLabel="Delete"
        busy={deleting}
        description={
          <>
            Are you sure you want to delete the category <strong>{delSpace?.name}</strong>?
            This will also delete all {delSpace?.query_count || 0}{' '}
            {delSpace?.query_count === 1 ? 'insight' : 'insights'} in it. This action cannot be undone.
          </>
        }
      />
    </Box>
  );
}
